import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { WaitListEntry } from 'src/models/appointment/_types';
import {
  checkAppointmentEligibility,
  createAppointmentVisit,
  createAppointmentWaitListEntry,
} from 'src/models/appointment/appointment';
import { User } from 'src/models/user/_types';
import { fetchUser, searchClients } from 'src/models/user/user';
import { Visit, VisitStatus } from 'src/models/visit/_types';

interface UserWithWarning extends User {
  warning?: string;
}

export const useAddUsersDialog = (appointment, onClose, showWaitList) => {
  const queryClient = useQueryClient();
  const [searchMode, setSearchMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [usersToAdd, setUsersToAdd] = useState<UserWithWarning[]>([]);
  const [userFetching, setUserFetching] = useState(false);
  // waitlist
  const [waitlistUsersToAdd, setWaitlistUsersToAdd] = useState<
    UserWithWarning[]
  >([]);

  const { data: searchResults, isFetching: searchFetching } = useQuery({
    queryKey: ['users', 'search', searchValue],

    queryFn: () => {
      if (searchValue) {
        return searchClients({ search: searchValue });
      } else {
        return [];
      }
    },
  });

  const searchUsers = searchResults?.filter((r) => {
    return (
      usersToAdd.every((u) => u.id !== r.id) &&
      waitlistUsersToAdd.every((u) => u.id !== r.id)
    );
  });

  const userList = showWaitList ? waitlistUsersToAdd : usersToAdd;

  const handleSearch = (searchValue) => {
    setSearchMode(true);
    setSearchValue(searchValue);
  };

  const handleResultClick = async (r) => {
    setSearchMode(false);
    setSearchValue('');
    setUserFetching(true);

    const user = await queryClient.fetchQuery({
      queryKey: ['users', 'user', r.id],
      queryFn: () => fetchUser({ id: r.id }),
    });

    if (showWaitList) {
      setWaitlistUsersToAdd([...waitlistUsersToAdd, user]);
    } else {
      const warning = await queryClient.fetchQuery({
        queryKey: [
          'appointment',
          appointment.id,
          'user',
          user.id,
          'eligibility',
        ],
        queryFn: () => checkAppointmentEligibility({ appointment, user }),
      });

      const userWithWarning: UserWithWarning = {
        ...user,
        warning: warning.reasonMessage,
      };
      setUsersToAdd([...usersToAdd, userWithWarning]);
    }

    setUserFetching(false);
  };

  const visitMutation = useMutation({
    mutationFn: (visit: Partial<Visit>) => {
      return createAppointmentVisit({ appointment, visit });
    },
  });

  const waitListMutation = useMutation({
    mutationFn: (waitListEntry: Partial<WaitListEntry>) => {
      return createAppointmentWaitListEntry({ appointment, waitListEntry });
    },
  });

  const handleAddUsers = async () => {
    setSaving(true);
    const visitPromises = usersToAdd.map((user) => {
      const visit = {
        chargeCancellation: false,
        chargeNoshow: false,
        status: VisitStatus.Reserved,
        userId: user.id,
      };
      return visitMutation.mutateAsync(visit);
    });

    const entryPromises = waitlistUsersToAdd.map((user) => {
      const entry = {
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        userId: user.id,
      };
      return waitListMutation.mutateAsync(entry);
    });
    await Promise.all([...visitPromises, ...entryPromises]);
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    toast.success('Users added successfully');
    setSaving(false);
    handleCancel();
  };

  const handleCancel = async () => {
    setUsersToAdd([]);
    setWaitlistUsersToAdd([]);
    onClose();
  };

  const handleRemoveUser = (user) => {
    if (showWaitList) {
      setWaitlistUsersToAdd(waitlistUsersToAdd.filter((u) => u.id !== user.id));
    } else {
      setUsersToAdd(usersToAdd.filter((u) => u.id !== user.id));
    }
  };

  const errorCount = usersToAdd.filter((u) => u.warning).length;

  return {
    searchMode,
    setSearchMode,
    searchFetching,
    searchUsers,
    searchValue,
    setSearchValue,
    handleSearch,
    handleResultClick,
    userList,
    setUsersToAdd,
    userFetching,
    errorCount,
    handleAddUsers,
    handleCancel,
    handleRemoveUser,
    saving,
  };
};
